import { Fragment, useMemo } from "react";
import { OrganizationType } from "@remi/enums/organization-type";
import { DateFormat, formatDate, formatDistanceToNowShort } from "@remi/formatting";
import { RnLastUpdated, RnPalmTree, RnSun12pmTo3pm } from "@remi/icons/next";
import { RemiSeparator } from "@remi/next-ui";
import { HourIcon } from "@remi/next-ui/utils";
import { can } from "@remi/permissions";
import { PersonStatus } from "@remi/types/personStatus";
import { PersonTagOptionDisplayNames } from "@remi/types/personTagOption";

import { useUserProfile } from "~/contexts/UserProfileContext";
import { useHomeownerSentimentScore } from "~/hooks/person/useHomeownerSentimentScore";
import { usePersonLastContact } from "~/hooks/person/usePersonLastContact";
import { useOrganization } from "~/hooks/useOrganization";
import { usePerson } from "~/hooks/usePerson";
import { PersonTagIcons } from "~/utils/records/person-tag-icons";
import { formatSentimentScore, getSentimentIcon } from "../../HomeownerSentimentIcon";

type StatusTagsProps = {
  personId: string;
  organizationId: string;
};

export const StatusTags = ({ personId, organizationId }: StatusTagsProps) => {
  const { data: person } = usePerson(personId);
  const { data: lastContact } = usePersonLastContact(personId);
  const { data: organization } = useOrganization(organizationId);

  const { user } = useUserProfile();
  const canViewDetails = can.person.viewDetails(user);
  const isHomeownerOrganization = organization?.type === OrganizationType.HOMEOWNER;
  const { data } = useHomeownerSentimentScore(organizationId, isHomeownerOrganization && canViewDetails);
  const sentimentScore = data?.sentimentScore;

  const [timeIcon, timeLabel] = useMemo(() => {
    if (person?.timeZone) {
      const date = new Date(new Date().toLocaleString("en-us", { timeZone: person.timeZone }));
      return [
        HourIcon[date.getHours()] ?? <RnSun12pmTo3pm key="time-icon" />,
        <>
          {formatDate(date, { format: DateFormat.TIME_AM_PM })}
          <span className="@max-[340px]/contact:hidden"> local time</span>
        </>,
      ];
    }
    return [undefined, "---"];
  }, [person?.timeZone]);

  if (!person || !organization) {
    return null;
  }

  return (
    <div className="flex w-full flex-wrap items-center gap-2">
      {!isHomeownerOrganization &&
        person.tags.map((tag) => (
          <Fragment key={tag.tag}>
            <StatusTag icon={PersonTagIcons[tag.tag]} label={PersonTagOptionDisplayNames[tag.tag]} />
            <RemiSeparator orientation="vertical" className="h-4" />
          </Fragment>
        ))}

      {person.status === PersonStatus.OUT_OF_OFFICE && (
        <>
          <StatusTag icon={<RnPalmTree />} label="Out of office" />
          <RemiSeparator orientation="vertical" className="h-4" />
        </>
      )}

      {isHomeownerOrganization && canViewDetails && (
        <>
          {sentimentScore !== undefined && sentimentScore !== null && (
            <>
              <StatusTag
                icon={getSentimentIcon({ score: sentimentScore })}
                label={formatSentimentScore(sentimentScore)}
              />
              <RemiSeparator orientation="vertical" className="h-4" />
            </>
          )}
          <StatusTag
            icon={<RnLastUpdated />}
            label={
              lastContact ? (
                <>
                  <span className="@max-[340px]/contact:hidden">Contact </span>
                  {formatDistanceToNowShort(lastContact, { suffix: "ago" })}
                </>
              ) : (
                "No contact"
              )
            }
          />
          <RemiSeparator orientation="vertical" className="h-4" />
        </>
      )}

      <StatusTag icon={timeIcon} label={timeLabel} />
    </div>
  );
};

const StatusTag = ({ icon, label }: { icon?: React.ReactNode; label: React.ReactNode }) => {
  return (
    <div className="flex items-center gap-0.5">
      {icon && <span className="text-icon-tertiary">{icon}</span>}
      <span className="text-text-secondary text-xs">{label}</span>
    </div>
  );
};
